import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import { extract_items, parse_response_data } from '@/utils/api_response_helpers';
import { fetch_questions_in_project } from '@/services/question_service';
import {
  fetch_entity_type_workflow_forms,
  fetch_sla_assigned_workflows,
  fetch_sla_workflow_statuses,
} from '@/services/workflow_service';
import {
  CREATE_SLA_TRIGGER_URL,
  LIST_SLA_CONFIGS_URL,
  LIST_SLA_TRIGGERS_URL,
  UPDATE_SLA_TRIGGER_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';

const DEFAULT_TRIGGER_FORM = () => ({
  id: null,
  trigger_type: 'status_change',
  workflow_status_id: '',
  section_question_id: '',
  auto_start: true,
});

const normalize_option = (item = {}, index = 0) => ({
  id: Number(
    item?.id
    ?? item?.status_id
    ?? item?.workflow_status_id
    ?? item?.question_id
    ?? item?.section_question_id
    ?? 0
  ) || null,
  name: String(
    item?.name
    ?? item?.status_name
    ?? item?.workflow_status_name
    ?? item?.question_text
    ?? item?.question
    ?? item?.text
    ?? `Option ${index + 1}`,
  ).trim(),
});

export default {
  name: 'ServiceLayerTrigger',
  mixins: [alert_mixin],
  data: () => ({
    loading: false,
    trigger_loading: false,
    trigger_saving: false,
    context_loading: false,
    selected_sla: null,
    sla_triggers: [],
    trigger_form: DEFAULT_TRIGGER_FORM(),
    sla_configs: [],
    workflow_status_options: [],
    question_options: [],
    selected_workflow_id: '',
    selected_form_id: '',
  }),
  mounted() {
    this.load_trigger_context_options();
  },
  watch: {
    '$route.query.sla_id': {
      immediate: true,
      handler() {
        this.load_selected_sla();
      },
    },
    '$route.query.workflowId': {
      handler() {
        this.load_trigger_context_options();
      },
    },
    '$route.query.workflow_id': {
      handler() {
        this.load_trigger_context_options();
      },
    },
    '$route.query.form_id': {
      handler() {
        this.load_trigger_context_options();
      },
    },
  },
  methods: {
    route_query_value(...keys) {
      for (const key of keys) {
        const value = this.$route?.query?.[key];
        if (value !== undefined && value !== null && String(value).trim()) {
          return String(value).trim();
        }
      }
      return '';
    },
    normalize_sla_item(item = {}) {
      const is_active = item.is_active !== false;
      const workflow_id = Number(
        item.workflow_id
        || item.entity_type_workflow
        || item.entity_type_workflow_id
        || item.sla_workflow_id
        || item.workflow
        || item.sla_workflow
        || 0
      ) || null;
      return {
        id: item.id,
        name: item.name || `SLA ${item.id}`,
        description: item.description || '',
        target_duration: item.target_duration || '-',
        warning_threshold: item.warning_threshold || '-',
        escalation_duration: item.escalation_duration || '-',
        workflow_id,
        workflow_name: item.workflow_name || item.entity_type_workflow_name || '',
        is_active,
        status_label: is_active ? 'Active' : 'Inactive',
        status_class: is_active ? 'sla-status-active' : 'sla-status-inactive',
        created_label: this.format_date(item.date_created || item.date_updated),
      };
    },
    format_date(date_value) {
      if (!date_value) return '-';
      const date = new Date(date_value);
      if (Number.isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    },
    format_duration(duration_value) {
      const raw_value = String(duration_value || '').trim();
      if (!raw_value || raw_value === '-') return '-';

      const [day_part, time_part] = raw_value.includes(' ')
        ? raw_value.split(/\s+/, 2)
        : ['0', raw_value];

      const days = Number(day_part);
      const [hours_raw, minutes_raw, seconds_raw] = String(time_part || '').split(':');
      const hours = Number(hours_raw);
      const minutes = Number(minutes_raw);
      const seconds = Number(seconds_raw);

      if ([days, hours, minutes, seconds].some((value) => Number.isNaN(value))) {
        return raw_value;
      }

      const parts = [];
      const total_hours = (days * 24) + hours;
      const total_days = Math.floor(total_hours / 24);
      const remaining_hours = total_hours % 24;

      if (total_days > 0) parts.push(`${total_days} ${total_days === 1 ? 'day' : 'days'}`);
      if (remaining_hours > 0) parts.push(`${remaining_hours} ${remaining_hours === 1 ? 'hour' : 'hours'}`);
      if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
      if (seconds > 0 && parts.length === 0) parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`);

      return parts.length ? parts.join(' ') : '0 minutes';
    },
    build_workflow_payload(workflow_id = null) {
      const normalized_workflow_id = Number(workflow_id || this.selected_workflow_id || 0);
      return {
        entity_workflow_id: normalized_workflow_id,
        entity_type_workflow_id: normalized_workflow_id,
        workflow_id: normalized_workflow_id,
      };
    },
    normalize_workflow_status(item = {}, index = 0) {
      return normalize_option({
        ...item,
        id: item?.id ?? item?.status_id ?? item?.workflow_status_id,
        name: item?.name || item?.status_name || item?.workflow_status_name || item?.status,
      }, index);
    },
    normalize_question_option(item = {}, index = 0) {
      return normalize_option({
        ...item,
        id: item?.section_question_id ?? item?.question_id ?? item?.id,
        name: item?.question_text || item?.label || item?.name || item?.text,
      }, index);
    },
    async load_trigger_context_options() {
      this.context_loading = true;
      try {
        this.selected_form_id = this.route_query_value('form_id');

        this.selected_workflow_id = this.route_query_value('workflowId', 'workflow_id', 'entity_workflow_id')
          || String(this.selected_sla?.workflow_id || '').trim();

        if (this.selected_workflow_id) {
          const [workflow_status_source, workflow_forms] = await Promise.all([
            fetch_sla_workflow_statuses(this.selected_workflow_id),
            fetch_entity_type_workflow_forms(this.build_workflow_payload(this.selected_workflow_id)),
          ]);

          const normalized_status_source = Array.isArray(workflow_status_source) ? workflow_status_source : [];
          this.workflow_status_options = normalized_status_source.map((status, index) => this.normalize_workflow_status(status, index));

          const assigned_forms = (Array.isArray(workflow_forms) ? workflow_forms : [])
            .map((form, index) => normalize_option({
              id: form?.id ?? form?.form_id,
              name: form?.form_name || form?.name || `Form ${index + 1}`,
            }, index))
            .filter((form) => form.id);

          if (!this.selected_form_id && assigned_forms.length) {
            this.selected_form_id = String(assigned_forms[0].id);
          }
        } else {
          this.workflow_status_options = [];
        }

        if (this.selected_form_id) {
          const questions = await fetch_questions_in_project({ form_id: Number(this.selected_form_id) });
          this.question_options = (Array.isArray(questions) ? questions : [])
            .map((question, index) => this.normalize_question_option(question, index))
            .filter((question) => question.id);
        } else {
          this.question_options = [];
        }
      } catch (error) {
        this.workflow_status_options = [];
        this.question_options = [];
        this.show_error(error?.response?.data?.message || error?.message || 'Failed to load trigger options.');
      } finally {
        this.context_loading = false;
      }
    },
    async load_selected_sla() {
      const sla_id = Number(this.$route?.query?.sla_id);
      if (!Number.isInteger(sla_id) || sla_id <= 0) {
        this.selected_sla = null;
        this.sla_triggers = [];
        return;
      }

      this.loading = true;
      try {
        const response = await axios.get(LIST_SLA_CONFIGS_URL, get_request_config());
        const response_data = parse_response_data(response);
        const items = extract_items(response).concat(
          Array.isArray(response_data?.items) ? response_data.items : [],
        );
        this.sla_configs = items.map((item) => this.normalize_sla_item(item));
        this.selected_sla = this.sla_configs.find((item) => String(item.id) === String(sla_id)) || null;
        if (this.selected_sla) {
          try {
            const assigned_workflows = await fetch_sla_assigned_workflows(this.selected_sla.id);
            const assigned_workflow = Array.isArray(assigned_workflows) && assigned_workflows.length
              ? assigned_workflows[0]
              : null;
            this.selected_sla = {
              ...this.selected_sla,
              workflow_id: assigned_workflow?.workflow_id || this.selected_sla.workflow_id || null,
              workflow_name: assigned_workflow?.workflow_name || this.selected_sla.workflow_name || '',
            };
          } catch {
            // Keep the base SLA details even if the binding lookup fails.
          }
          await this.load_sla_triggers();
          await this.load_trigger_context_options();
        }
      } catch (error) {
        this.selected_sla = null;
        this.sla_triggers = [];
        this.show_error(error?.response?.data?.message || error?.message || 'Failed to load SLA.');
      } finally {
        this.loading = false;
      }
    },
    normalize_trigger_item(item = {}) {
      const trigger_condition = item?.trigger_condition || {};
      return {
        id: item?.id ?? null,
        sla_config: item?.sla_config ?? item?.sla_config_id ?? this.selected_sla?.id ?? null,
        sla_name: item?.sla_name || this.selected_sla?.name || '',
        trigger_type: item?.trigger_type || 'status_change',
        workflow_status_id: item?.workflow_status ?? item?.workflow_status_id ?? '',
        workflow_status_name: item?.workflow_status_name || '-',
        section_question: item?.section_question ?? null,
        section_question_text: item?.section_question_text || null,
        section_question_id: item?.section_question_id ?? item?.section_question ?? '',
        trigger_condition: {
          auto_start: Boolean(trigger_condition?.auto_start ?? item?.auto_start ?? false),
          ...trigger_condition,
        },
        date_created: item?.date_created || null,
      };
    },
    dedupe_trigger_items(items = []) {
      const seen = new Set();
      return items.filter((item) => {
        const key = item?.id != null ? `id:${item.id}` : JSON.stringify({
          sla_config: item?.sla_config,
          trigger_type: item?.trigger_type,
          workflow_status_id: item?.workflow_status_id,
          section_question_id: item?.section_question_id,
          date_created: item?.date_created,
        });
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    async load_sla_triggers() {
      if (!this.selected_sla?.id) {
        this.sla_triggers = [];
        return;
      }

      this.trigger_loading = true;
      try {
        const response = await axios.get(
          LIST_SLA_TRIGGERS_URL,
          get_request_config({
            params: {
              sla_config_id: this.selected_sla.id,
            },
          }),
        );
        const response_data = parse_response_data(response);
        const items = this.dedupe_trigger_items(extract_items(response).concat(
          Array.isArray(response_data?.data) ? response_data.data : [],
          Array.isArray(response_data?.triggers) ? response_data.triggers : [],
          Array.isArray(response_data?.data?.triggers) ? response_data.data.triggers : [],
          Array.isArray(response_data?.data?.items) ? response_data.data.items : [],
        ));
        this.sla_triggers = items.map((item) => this.normalize_trigger_item(item));
      } catch {
        this.sla_triggers = [];
      } finally {
        this.trigger_loading = false;
      }
    },
    edit_trigger(trigger) {
      this.trigger_form = {
        id: trigger?.id ?? null,
        trigger_type: trigger?.trigger_type || 'status_change',
        workflow_status_id: trigger?.workflow_status_id || '',
        section_question_id: trigger?.section_question_id || trigger?.section_question || '',
        auto_start: Boolean(trigger?.trigger_condition?.auto_start),
      };
      this.$nextTick(() => {
        const target = document.getElementById('sla_trigger_form');
        if (target?.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    handle_trigger_type_change() {
      if (this.trigger_form.trigger_type === 'status_change') {
        this.trigger_form.section_question_id = '';
      } else if (this.trigger_form.trigger_type === 'question_answered') {
        this.trigger_form.workflow_status_id = '';
      } else {
        this.trigger_form.workflow_status_id = '';
        this.trigger_form.section_question_id = '';
      }
    },
    build_trigger_payload() {
      const payload = {
        sla_trigger_id: this.trigger_form?.id ? Number(this.trigger_form.id) : undefined,
        sla_config_id: Number(this.selected_sla?.id),
        trigger_type: String(this.trigger_form.trigger_type || 'status_change'),
        trigger_condition: { auto_start: Boolean(this.trigger_form.auto_start) },
        workflow_status_id: null,
        section_question_id: null,
      };
      if (this.trigger_form.trigger_type === 'status_change') {
        payload.workflow_status_id = Number(this.trigger_form.workflow_status_id);
      }
      if (this.trigger_form.trigger_type === 'question_answered') {
        payload.section_question_id = Number(this.trigger_form.section_question_id);
      }
      return payload;
    },
    validate_trigger_form() {
      if (!this.selected_sla?.id) {
        this.show_error('Select an SLA first.');
        return false;
      }
      if (!this.trigger_form.trigger_type) {
        this.show_error('Trigger type is required.');
        return false;
      }
      if (this.trigger_form.trigger_type === 'status_change') {
        const workflow_status_id = Number(this.trigger_form.workflow_status_id);
        if (!Number.isInteger(workflow_status_id) || workflow_status_id <= 0) {
          this.show_error('Workflow status is required.');
          return false;
        }
      }
      if (this.trigger_form.trigger_type === 'question_answered') {
        const section_question_id = Number(this.trigger_form.section_question_id);
        if (!Number.isInteger(section_question_id) || section_question_id <= 0) {
          this.show_error('Question is required.');
          return false;
        }
      }
      return true;
    },
    async save_trigger() {
      if (!this.validate_trigger_form()) return;

      this.trigger_saving = true;
      const is_editing = Boolean(this.trigger_form?.id);
      const loader = await this.show_loader(is_editing ? 'Updating trigger...' : 'Creating trigger...');
      try {
        const payload = this.build_trigger_payload();
        const endpoint = is_editing ? UPDATE_SLA_TRIGGER_URL : CREATE_SLA_TRIGGER_URL;
        const response = await axios.post(
          endpoint,
          payload,
          get_request_config(),
        );
        const response_data = parse_response_data(response);
        if (response_data?.status && response_data.status !== 'success') {
          const error_message = response_data?.message || (is_editing ? 'Failed to update trigger.' : 'Failed to save trigger.');
          this.show_error(error_message);
          const save_result = false;
          return save_result;
        }
        this.trigger_form = DEFAULT_TRIGGER_FORM();
        await this.load_sla_triggers();
        this.show_success(response_data?.message || (is_editing ? 'Trigger updated successfully.' : 'Trigger saved successfully.'));
      } catch (error) {
        const response_data = parse_response_data(error?.response?.data || {});
        this.show_error(response_data?.message || error?.message || 'Failed to save trigger.');
      } finally {
        await this.close_loader(loader);
        this.trigger_saving = false;
      }
    },
    go_back() {
      this.$router.push({ name: 'ServiceLayerAgreement' });
    },
  },
};
